/**
 * Low-level API calls — all HTTP requests go through here.
 * Uses fetch only (no auth tokens; public website).
 */

function getApiBaseUrl() {
  const base = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/$/, "");
  if (!base) {
    throw new Error("Missing REACT_APP_BASE_URL");
  }
  return base;
}

/**
 * POST JSON to a path relative to API base (e.g. "user/homenew").
 * @param {string} path - path without leading slash
 * @param {object} body
 */
const guestFetchInit = {
  credentials: "omit",
};

function guestApiSuccess(status) {
  return (
    status === true ||
    status === 1 ||
    status === "true" ||
    status === "1"
  );
}

function looksLikeMissingAuthMessage(message) {
  return /authorization|token\s*missing|unauthorized|bearer/i.test(
    String(message || "")
  );
}

function toApiIsoDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return raw;
  if (raw.includes("T")) {
    const dt = new Date(raw);
    if (!Number.isNaN(dt.getTime())) return dt.toISOString();
    return raw;
  }
  const ymdMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0)).toISOString();
  }
  const dt = new Date(raw);
  if (!Number.isNaN(dt.getTime())) return dt.toISOString();
  return raw;
}

export async function postJson(path, body = {}) {
  const url = `${getApiBaseUrl()}/${path.replace(/^\//, "")}`;
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    ...guestFetchInit,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || json.error || res.statusText || "Request failed");
  }
  return json;
}

/**
 * GET JSON from a path relative to API base (e.g. `user/getdetailevent/:id`).
 * Same as Vibes mobile web `apiGet`.
 * @param {string} path
 */
export async function getJson(path) {
  const url = `${getApiBaseUrl()}/${path.replace(/^\//, "")}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    ...guestFetchInit,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.message || json.error || res.statusText || "Request failed");
  }
  return json;
}

/**
 * Event / table detail — guest flow (no auth token).
 * Prefer POST + `{ userId: "" }` (same guest pattern as `viewdetails`); fall back to GET
 * if the server only exposes the mobile-style GET route.
 * @param {string} eventId
 */
export async function getDetailEvent(eventId) {
  const path = `user/viewdetails/${eventId}`;
  try {
    const postRes = await postJson(path, { userId: "" });
    if (guestApiSuccess(postRes?.status)) return postRes;
    if (looksLikeMissingAuthMessage(postRes?.message)) {
      return await getJson(path);
    }
    return postRes;
  } catch (first) {
    try {
      return await getJson(path);
    } catch {
      throw first;
    }
  }
}

/**
 * POST user/viewdetails/:clubId — same as mobile app.
 * @param {string} clubId
 * @param {string} [userId]
 */
export function postViewDetails(clubId, userId = "") {
  return postJson(`user/viewdetails/${clubId}`, { userId: userId || "" });
}

/**
 * POST user/hostprofile — host public profile + events (pass host id as hostId).
 * @param {string} hostId
 */
export function postHostProfile(hostId) {
  return postJson("user/hostprofile", { hostId });
}

/**
 * POST user/checkbookeddate
 * Mirrors mobile payload for date + slot availability.
 */
export function postCheckBookedDate({
  eventId,
  startDate,
  endDate,
  isTable = "yes",
  userId = "",
  maleCount,
  femaleCount,
  timeSlot = "",
  type,
  alcoholic = [],
  nonAlcoholic = [],
  bottleServiceId = [],
  ticketid = "",
  ticketId = "",
  boothid = "",
  boothId = "",
}) {
  const params = {
    eventId,
    startDate: toApiIsoDate(startDate),
    endDate: toApiIsoDate(endDate),
    userId: userId || "",
    isTable,
    type: String(type || (isTable === "yes" ? "table" : "")),
    maleCount: Number(maleCount || 0),
    femaleCount: Number(femaleCount || 0),
    timeSlot: String(timeSlot || ""),
    alcoholic: Array.isArray(alcoholic) ? alcoholic : [],
    nonAlcoholic: Array.isArray(nonAlcoholic) ? nonAlcoholic : [],
    bottleServiceId: Array.isArray(bottleServiceId) ? bottleServiceId : [],
  };
  const resolvedTicketId = ticketId || ticketid;
  const resolvedBoothId = boothId || boothid;
  if (resolvedTicketId) params.ticketId = resolvedTicketId;
  if (resolvedBoothId) params.boothId = resolvedBoothId;
  return postJson("user/checkbookeddate", params);
}

/**
 * POST user/reviewsummary
 * Used before booking to get fee breakdown.
 */
export function postReviewSummary({
  eventid,
  //userId = "",
  boothId = "",
  boothid = "",
  members = 1,
  days = 1,
  promocode = "",
  ticketid = "",
  maleFemalePrice = 0,
  startDate,
  endDate,
}) {
  const toDdMmYy = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
    if (!match) return value;
    const [, year, month, day] = match;
    return `${day}-${month}-${year.slice(-2)}`;
  };

  const payload = {
    eventid,
    members,
    days,
    startDate: toDdMmYy(startDate),
    endDate: toDdMmYy(endDate),
  };
  const resolvedBoothId = boothId || boothid;
  if (resolvedBoothId) payload.boothid = resolvedBoothId;
  if (promocode) payload.promocode = promocode;
  if (ticketid) payload.ticketid = ticketid;
  if (maleFemalePrice) payload.maleFemalePrice = maleFemalePrice;
  return postJson("user/reviewsummary", payload);
}

/**
 * POST user/reviewsummary for bottle service flow.
 * Uses YYYY-MM-DD dates and bottle arrays as provided by backend contract.
 */
export function postBottleReviewSummary({
  eventid,
  days = 1,
  startDate,
  endDate,
  alcoholic = [],
  nonAlcoholic = [],
  promocode = "",
}) {
  return postJson("user/reviewsummary", {
    eventid,
    days,
    startDate,
    endDate,
    alcoholic,
    nonAlcoholic,
    promocode,
  });
}

function toReviewDdMmYy(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  if (!match) return value;
  const [, year, month, day] = match;
  return `${day}-${month}-${year.slice(-2)}`;
}

/**
 * POST user/reviewsummary for booth + bottle service flow.
 */
export function postBoothBottleReviewSummary({
  eventid,
  bottleId = "",
  members = 1,
  days = 1,
  startDate,
  endDate,
  alcoholic = [],
  nonAlcoholic = [],
  promocode = "",
}) {
  const payload = {
    eventid,
    members,
    days,
    startDate: toReviewDdMmYy(startDate),
    endDate: toReviewDdMmYy(endDate),
    alcoholic: Array.isArray(alcoholic) ? alcoholic : [],
    nonAlcoholic: Array.isArray(nonAlcoholic) ? nonAlcoholic : [],
  };
  if (bottleId) payload.bottleId = bottleId;
  if (promocode) payload.promocode = promocode;
  return postJson("user/reviewsummary", payload);
}

/**
 * POST user/promocodelist
 * Fetch promo codes for a host.
 */
export function postPromoCodeList(hostId) {
  return postJson("user/promocodelist", { hostId });
}

/**
 * POST user/booking
 * Final booking create call (same endpoint used by mobile app).
 */
export function postCreateBooking(payload) {
  return postJson("user/booking", {
    userId: "",
    ...(payload || {}),
  });
}

/**
 * POST user/webbooking
 * Final website booking call with Stripe payment method id.
 */
export function postWebBooking(payload) {
  return postJson("user/webbooking", {
    userId: "",
    ...(payload || {}),
  });
}

/**
 * GET user/category
 * Fetches discover categories shown on home page.
 */
export function getUserCategory() {
  return getJson("user/category");
}

/**
 * Build POST user/homenew body. Only includes optional fields when set.
 */
export function buildHomeNewPayload({
  lat,
  long,
  userId = "",
  categoryid = "",
  search_keyword = "",
}) {
  const payload = {
    lat: String(lat || ""),
    long: String(long || ""),
  };
  if (userId) payload.userId = String(userId);
  if (categoryid) payload.categoryid = String(categoryid);
  const kw = String(search_keyword || "").trim();
  if (kw) payload.search_keyword = kw;
  return payload;
}

/**
 * POST user/homenew
 * Fetches home listing with optional category/search filters.
 */
export function postHomeNew(params) {
  return postJson("user/homenew", buildHomeNewPayload(params));
}
